// @owners { team: patients }
import { Icon, ListDescription, ListItem, Switch } from '@alto/design-system';
import React from 'react';
import { updateAutoRefill } from '~shared/actions/prescriptions';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { useNextAvailableDatesForPrescriptions } from '~shared/features/next-available-date/queries/useNextAvailableDatesForPrescriptions';
import { formatRelativeDate } from '~shared/helpers/date';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getOrigin from '~shared/lib/analytics/src/getOrigin';
import { useDispatchShared } from '~shared/store';
import { type Prescription } from '~shared/types';
import { useUpdateCartItem } from '../../cart';
import { ORDER_TYPE_CONTEXT, type OrderTypeContext } from '../constants';

type AutoRefillToggleProps = {
  readonly handleToggleAutoRefill?: () => void;
  readonly orderTypeContext: OrderTypeContext;
  readonly prescription: Prescription;
};

export const AutoRefillToggle = ({ handleToggleAutoRefill, orderTypeContext, prescription }: AutoRefillToggleProps) => {
  const dispatch = useDispatchShared();
  const { handleUpdateCartItem } = useUpdateCartItem();
  const { trackEvent } = useAnalytics();
  // the auto refill status exists on the prescription itself if we're not in the cart
  const prescriptionID = prescription.id;
  const isPostCheckout = orderTypeContext === ORDER_TYPE_CONTEXT.POST_CHECKOUT;
  const { cartAutoRefills } = useCartData();
  const cartAutoRefill = cartAutoRefills[prescriptionID];
  const isAutoRefillOn = isPostCheckout ? !!prescription?.auto_refill : cartAutoRefill;
  const { nextAvailableDate } = useNextAvailableDatesForPrescriptions({ prescriptionID });
  const nextAutoRefillDate = nextAvailableDate?.nextAutoRefill || '';

  const onValueChange = (value: boolean) => {
    if (isPostCheckout) {
      dispatch(updateAutoRefill(prescription.id, value));
    } else {
      handleUpdateCartItem({ resource_id: prescriptionID, resource_type: 'prescription', auto_refill_enabled: value });
    }

    handleToggleAutoRefill?.();

    if (isAutoRefillOn !== value) {
      trackEvent({
        event: EVENTS.AUTO_REFILL_UI_STATUS_CHANGED,
        params: {
          origin: getOrigin(),
          auto_refill_status: value ? 'auto refill' : 'one-time fill',
        },
        additionalFields: {
          prescriptionId: prescriptionID,
        },
      });
    }
  };

  const description = isAutoRefillOn
    ? `Est. next refill: ${formatRelativeDate(nextAutoRefillDate, { includeRelative: true })}`
    : 'Receive automatic refills and bundled deliveries when possible.';

  return (
    <ListItem
      title="Auto Refill"
      LeftContent={<Icon name="sync" />}
      RightContent={
        <Switch
          value={isAutoRefillOn}
          onValueChange={onValueChange}
        />
      }
      descriptions={<ListDescription>{description}</ListDescription>}
      onPress={() => undefined}
    />
  );
};
